<template>
  <div class="app-container">
    <el-card shadow="never" class="query-card">
      <div v-show="showSearch" class="report-filter-panel">
      <!-- 第一行：消耗日期、命名策略、素材条件（与书籍汇总/策略分析一致：指标单独折叠） -->
      <el-form :model="query" ref="queryForm" :inline="true" class="glass-form">
        <el-form-item :label="$t('common.consumptionDate')" prop="dateRange">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            size="small"
            style="width: 280px"
            :range-separator="$t('common.dateRangeSep')"
            :start-placeholder="$t('common.dateStart')"
            :end-placeholder="$t('common.dateEnd')"
            value-format="yyyy-MM-dd"
            :picker-options="datePickerOptions"
            @change="handleDateChange"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.namingStrategy')">
          <el-select
            v-model="query.strategyId"
            clearable
            filterable
            :placeholder="$t('consumption.namingStrategyPlaceholder')"
            size="small"
            style="width: 200px"
          >
            <el-option v-for="s in strategyList" :key="s.id" :label="s.strategyName" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('consumption.materialName')" prop="name">
          <el-input
            v-model="query.name"
            :placeholder="$t('consumption.materialNamePlaceholder')"
            clearable
            size="small"
            style="width: 160px"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.numericId')" prop="ids">
          <el-select
            v-model="query.ids"
            multiple
            filterable
            allow-create
            default-first-option
            clearable
            collapse-tags
            :reserve-keyword="true"
            size="small"
            style="width: 220px"
            :placeholder="$t('consumption.numericIdPlaceholder')"
            @change="onMaterialIdsChange"
            @paste.native="onMaterialIdsPaste"
          >
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('consumption.materialId')" prop="externalId">
          <el-input
            v-model="query.externalId"
            :placeholder="$t('consumption.materialIdPlaceholder')"
            clearable
            size="small"
            style="width: 160px"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.materialType')" prop="type">
          <el-select v-model="query.type" :placeholder="$t('common.all')" clearable size="small" style="width: 100px">
            <el-option :label="$t('common.image')" value="image" />
            <el-option :label="$t('common.video')" value="video" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('consumption.tag')" prop="tagIds">
          <el-select
            v-model="query.tagIds"
            multiple
            clearable
            filterable
            collapse-tags
            size="small"
            style="width: 220px"
            :placeholder="$t('consumption.tagPlaceholder')"
          >
            <el-option
              v-for="tag in tagOptions"
              :key="tag.id"
              :label="tag.name"
              :value="tag.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('consumption.uploader')" prop="createdBy">
          <el-select
            v-model="query.createdBy"
            clearable
            filterable
            :placeholder="$t('consumption.uploaderPlaceholder')"
            size="small"
            style="width: 140px"
          >
            <el-option v-for="name in uploaderOptions" :key="name" :label="name" :value="name" />
          </el-select>
        </el-form-item>
      </el-form>
      <!-- 第二行：账户/书名/广告/投手 + 指标筛选 + 操作 -->
      <el-form :model="query" :inline="true" class="glass-form">
        <el-form-item label="投放平台">
          <el-select v-model="query.platform" clearable placeholder="全部" size="small" style="width: 130px">
            <el-option label="Meta" value="meta" />
            <el-option label="AppLovin" value="applovin" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('consumption.adAccountId')">
          <el-input
            v-model="query.adAccountId"
            :placeholder="$t('consumption.adAccountIdPlaceholder')"
            clearable
            size="small"
            style="width: 160px"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item :label="$t('common.bookName')">
          <el-input
            v-model="query.bookName"
            :placeholder="$t('common.bookNameSearchPlaceholder')"
            clearable
            size="small"
            style="width: 160px"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.adId')">
          <el-input
            v-model="query.fbAdId"
            :placeholder="$t('consumption.adIdPlaceholder')"
            clearable
            size="small"
            style="width: 160px"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item v-if="!isDataLimited" label="投手">
          <el-select
            v-model="query.userName"
            clearable
            filterable
            placeholder="全部投手"
            size="small"
            style="width: 140px"
          >
            <el-option v-for="name in creatorOptions" :key="name" :label="name" :value="name" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="text" @click="showMetricFilter = !showMetricFilter">
            {{ showMetricFilter ? $t('consumption.hideMetricFilter') : $t('consumption.metricFilter') }}
            <i :class="showMetricFilter ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </el-button>
          <el-button type="primary" icon="el-icon-search" size="mini" @click="handleQuery">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" size="mini" @click="resetQuery">{{ $t('common.reset') }}</el-button>
        </el-form-item>
        <el-form-item v-if="queryInfo">
          <el-tag type="info" size="small">
            {{ queryInfo.engine.toUpperCase() }} | {{ queryInfo.queryMs }}ms | {{ queryInfo.total }}
          </el-tag>
        </el-form-item>
      </el-form>
      </div>
      <!-- 指标范围筛选（与书籍汇总「指标筛选」同款折叠区） -->
      <el-form
        v-show="showMetricFilter && showSearch"
        :model="query"
        :inline="true"
        class="glass-form metric-filter-row"
      >
        <el-form-item :label="$t('consumption.adCountMin')">
          <el-input-number
            v-model="query.adCountMin"
            :min="0"
            size="small"
            style="width: 120px"
            :placeholder="$t('consumption.adCountMinPlaceholder')"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.adCountMax')">
          <el-input-number
            v-model="query.adCountMax"
            :min="0"
            size="small"
            style="width: 120px"
            :placeholder="$t('consumption.adCountMaxPlaceholder')"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.spendMin')">
          <el-input-number
            v-model="query.spendMin"
            :precision="2"
            :min="0"
            size="small"
            style="width: 120px"
            :placeholder="$t('consumption.spendMinPlaceholder')"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.spendMax')">
          <el-input-number
            v-model="query.spendMax"
            :precision="2"
            :min="0"
            size="small"
            style="width: 120px"
            :placeholder="$t('consumption.spendMaxPlaceholder')"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.ctrMin')">
          <el-input-number
            v-model="ctrMinPercent"
            :precision="2"
            :min="0"
            :max="100"
            size="small"
            style="width: 110px"
            placeholder="%"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.ctrMax')">
          <el-input-number
            v-model="ctrMaxPercent"
            :precision="2"
            :min="0"
            :max="100"
            size="small"
            style="width: 110px"
            placeholder="%"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.purchasesMin')">
          <el-input-number
            v-model="query.purchasesMin"
            :min="0"
            size="small"
            style="width: 120px"
            :placeholder="$t('consumption.purchasesMinPlaceholder')"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.purchasesMax')">
          <el-input-number
            v-model="query.purchasesMax"
            :min="0"
            size="small"
            style="width: 120px"
            :placeholder="$t('consumption.purchasesMaxPlaceholder')"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.roiMin')">
          <el-input-number
            v-model="query.roiMin"
            :precision="4"
            size="small"
            style="width: 120px"
            :placeholder="$t('consumption.roiMinPlaceholder')"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.roiMax')">
          <el-input-number
            v-model="query.roiMax"
            :precision="4"
            size="small"
            style="width: 120px"
            :placeholder="$t('consumption.roiMaxPlaceholder')"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.onlyWithSpend')">
          <el-switch v-model="query.onlyWithConsumption" :active-text="$t('common.yes')" :inactive-text="$t('common.no')" @change="handleQuery" />
        </el-form-item>
        <el-form-item :label="$t('consumption.costPerPurchaseMin')">
          <el-input-number
            v-model="query.costPerPurchaseMin"
            :precision="2"
            :min="0"
            size="small"
            style="width: 120px"
            :placeholder="$t('consumption.costPerPurchaseMinPlaceholder')"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.costPerPurchaseMax')">
          <el-input-number
            v-model="query.costPerPurchaseMax"
            :precision="2"
            :min="0"
            size="small"
            style="width: 120px"
            :placeholder="$t('consumption.costPerPurchaseMaxPlaceholder')"
            controls-position="right"
          />
        </el-form-item>
      </el-form>

      <table-operations :show-add="false" :show-delete="false">
        <el-button
          type="primary"
          plain
          icon="el-icon-folder"
          size="mini"
          :disabled="selection.length === 0"
          :loading="moveSaving"
          v-hasPermi="['material:material:update']"
          @click="handleBatchMove"
        >{{ $t('consumption.batchMoveToFolder') }}</el-button>
        <el-button
          type="warning"
          plain
          icon="el-icon-download"
          size="mini"
          :loading="exporting"
          @click="handleExport"
        >{{ $t('consumption.exportExcel') }}</el-button>
        <template slot="right-toolbar">
          <right-toolbar :showSearch.sync="showSearch" @queryTable="fetchList"></right-toolbar>
        </template>
      </table-operations>

      <!-- 无数据提示 -->
      <el-alert
        v-if="!loading && materialList.length === 0 && !ckDiagnostic"
        :title="$t('consumption.noDataTitle')"
        :description="$t('consumption.noDataDesc')"
        type="info"
        show-icon
        :closable="false"
        style="margin-bottom: 12px"
      />
      <!-- CK 诊断信息 -->
      <el-alert
        v-if="!loading && ckDiagnostic"
        :title="ckDiagnostic.conclusion || 'CK query returned no data'"
        type="warning"
        show-icon
        :closable="false"
        style="margin-bottom: 12px"
      >
        <div slot="description" style="font-size: 12px; line-height: 1.8;">
          <div>Mapping total: <b>{{ ckDiagnostic.mappingTotal }}</b> | Mapping filtered: <b>{{ ckDiagnostic.mappingFiltered }}</b></div>
          <div>CK ad rows (date range): <b>{{ ckDiagnostic.ckAdRowCount }}</b> | CK ad rows (all): <b>{{ ckDiagnostic.ckAdTotalCount }}</b></div>
          <div v-if="ckDiagnostic.ckObjectIdSample">CK object_id sample: {{ ckDiagnostic.ckObjectIdSample.join(', ') }}</div>
          <div v-if="ckDiagnostic.mappingFbAdIdSample">Mapping fb_ad_id sample: {{ ckDiagnostic.mappingFbAdIdSample.join(', ') }}</div>
          <div v-if="ckDiagnostic.ckError" style="color: #f56c6c;">CK error: {{ ckDiagnostic.ckError }}</div>
        </div>
      </el-alert>

      <!-- 数据表格 -->
      <el-table
        v-loading="loading"
        :data="materialList"
        row-key="id"
        border
        @selection-change="handleSelectionChange"
        :header-cell-style="{ background: '#f8fafd', color: '#606266', fontWeight: '600' }"
        @sort-change="handleSortChange"
      >
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column type="index" label="#" width="50" align="center" />
        <el-table-column :label="$t('consumption.material')" min-width="240">
          <template slot-scope="{ row }">
            <div
              class="material-cell"
              :data-ads-file-url="row.filePath"
              :data-ads-thumbnail-url="row.thumbnail || ''"
              :data-ads-material-type="row.type"
            >
              <img
                v-if="row.type === 'image'"
                :src="row.filePath"
                class="material-thumb"
                @click="handlePreview(row)"
              />
              <video
                v-else
                :src="row.filePath"
                :poster="row.thumbnail"
                class="material-thumb"
                @click="handlePreview(row)"
              ></video>
              <div class="material-info">
                <div class="material-name" :title="row.name">{{ row.name }}</div>
                <el-tag :type="row.type === 'image' ? 'success' : 'primary'" size="mini">
                  {{ row.type === 'image' ? $t('common.image') : $t('common.video') }}
                </el-tag>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.numericId')" width="100" prop="id" align="center" />
        <el-table-column :label="$t('consumption.materialId')" width="140" prop="externalId" show-overflow-tooltip />
        <el-table-column :label="$t('consumption.tag')" min-width="180">
          <template slot-scope="{ row }">
            <template v-if="splitTagNames(row.tagNames).length > 0">
              <el-tag
                v-for="tagName in splitTagNames(row.tagNames)"
                :key="tagName"
                size="mini"
                style="margin-right: 6px; margin-bottom: 4px;"
              >
                {{ tagName }}
              </el-tag>
            </template>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.spend')" width="110" align="right" sortable="custom" prop="spend">
          <template slot-scope="{ row }">
            {{ formatMoney(row.spend) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.impressions')" width="100" align="right" sortable="custom" prop="impressions">
          <template slot-scope="{ row }">
            {{ formatInt(row.impressions) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.clicks')" width="90" align="right" sortable="custom" prop="clicks">
          <template slot-scope="{ row }">
            {{ formatInt(row.clicks) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.ctr')" width="90" align="right" sortable="custom" prop="ctr">
          <template slot-scope="{ row }">
            {{ formatPercent(row.ctr) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.cpm')" width="110" align="right" sortable="custom" prop="cpm">
          <template slot-scope="{ row }">
            {{ formatMoney(row.cpm) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.cpc')" width="120" align="right" sortable="custom" prop="cpc">
          <template slot-scope="{ row }">
            {{ formatMoney(row.cpc) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.purchases')" width="110" align="right" sortable="custom" prop="purchases">
          <template slot-scope="{ row }">
            {{ formatInt(row.purchases) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.costPerPurchase')" width="120" align="right" sortable="custom" prop="cost_per_purchase">
          <template slot-scope="{ row }">
            {{ formatMoney(row.costPerPurchase) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.purchaseValue')" width="120" align="right" sortable="custom" prop="purchase_value">
          <template slot-scope="{ row }">
            {{ formatMoney(row.purchaseValue) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.roi')" width="80" align="right" sortable="custom" prop="roi">
          <template slot-scope="{ row }">
            {{ formatDecimal(row.roi) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.adCount')" width="100" align="center" sortable="custom" prop="ad_count">
          <template slot-scope="{ row }">
            {{ row.adCount != null ? row.adCount : '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('consumption.mimeType')" width="110" align="center" prop="mimeType" show-overflow-tooltip />
        <el-table-column :label="$t('common.createTime')" width="160" align="center" prop="createdAt" sortable="custom" />
        <el-table-column :label="$t('common.operation')" align="center" fixed="right" width="100">
          <template slot-scope="{ row }">
            <el-button size="mini" type="text" icon="el-icon-data-analysis" @click="handleDetail(row)">{{ $t('common.detail') }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="summary-row" v-if="materialList.length > 0">
        <span class="summary-label">{{ $t('consumption.summaryLabel', { total: total }) }}</span>
        <span>{{ $t('consumption.spend') }} <b>{{ formatMoney(summary.spend) }}</b></span>
        <span>{{ $t('consumption.impressions') }} <b>{{ formatInt(summary.impressions) }}</b></span>
        <span>{{ $t('consumption.clicks') }} <b>{{ formatInt(summary.clicks) }}</b></span>
        <span>{{ $t('consumption.purchasesShort') }} <b>{{ formatInt(summary.purchases) }}</b></span>
        <span>{{ $t('consumption.purchaseValueShort') }} <b>{{ formatMoney(summary.purchaseValue) }}</b></span>
      </div>

      <pagination
        v-show="total > 0"
        :total="total"
        :page.sync="query.pageNo"
        :limit.sync="query.pageSize"
        @pagination="fetchList"
      />
    </el-card>

    <!-- 每日明细对话框 -->
    <el-dialog
      :title="$t('consumption.dailyDetailTitle') + (detailMaterial ? detailMaterial.name : '')"
      :visible.sync="detailVisible"
      width="900px"
      append-to-body
    >
      <el-table :data="dailyList" v-loading="detailLoading" border size="small">
        <el-table-column prop="date" :label="$t('consumption.date')" width="120" align="center" />
        <el-table-column :label="$t('consumption.spend')" width="100" align="right">
          <template slot-scope="{ row }">{{ formatMoney(row.spend) }}</template>
        </el-table-column>
        <el-table-column :label="$t('consumption.impressionsShort')" width="100" align="right">
          <template slot-scope="{ row }">{{ formatInt(row.impressions) }}</template>
        </el-table-column>
        <el-table-column :label="$t('consumption.clicksShort')" width="90" align="right">
          <template slot-scope="{ row }">{{ formatInt(row.clicks) }}</template>
        </el-table-column>
        <el-table-column :label="$t('consumption.cpm')" width="90" align="right">
          <template slot-scope="{ row }">{{ formatMoney(row.cpm) }}</template>
        </el-table-column>
        <el-table-column :label="$t('consumption.cpc')" width="90" align="right">
          <template slot-scope="{ row }">{{ formatMoney(row.cpc) }}</template>
        </el-table-column>
        <el-table-column :label="$t('consumption.ctr')" width="80" align="right">
          <template slot-scope="{ row }">{{ formatPercent(row.ctr) }}</template>
        </el-table-column>
        <el-table-column :label="$t('consumption.purchasesShort')" width="70" align="right">
          <template slot-scope="{ row }">{{ formatInt(row.purchases) }}</template>
        </el-table-column>
        <el-table-column :label="$t('consumption.purchaseValueShort')" width="100" align="right">
          <template slot-scope="{ row }">{{ formatMoney(row.purchaseValue) }}</template>
        </el-table-column>
        <el-table-column :label="$t('consumption.roi')" width="80" align="right">
          <template slot-scope="{ row }">{{ formatDecimal(row.roi) }}</template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button @click="detailVisible = false">{{ $t('common.close') }}</el-button>
      </div>
    </el-dialog>

    <!-- 批量移动对话框 -->
    <el-dialog
      :title="$t('consumption.batchMoveDialogTitle')"
      :visible.sync="moveDialogVisible"
      width="460px"
      append-to-body
    >
      <div class="batch-move-tip">
        <i class="el-icon-info" style="margin-right: 4px;"></i>
        {{ $t('consumption.batchMoveTip', { count: selection.length }) }}
      </div>
      <div class="batch-move-tree-wrap">
        <div
          class="batch-move-item"
          :class="{ 'is-active': moveForm.folderId === 0 }"
          @click="selectMoveFolder(0)"
        >
          <i class="el-icon-folder-opened"></i>
          <span>{{ $t('consumption.unfiled') }}</span>
          <i v-if="moveForm.folderId === 0" class="el-icon-check batch-move-check"></i>
        </div>
        <el-tree
          :data="moveFolderTreeData"
          :props="{ children: 'children', label: 'name' }"
          node-key="id"
          :highlight-current="false"
          :expand-on-click-node="false"
          :default-expand-all="false"
          class="move-folder-tree"
          @node-click="onMoveFolderTreeClick"
        >
          <span slot-scope="{ data }" class="batch-move-tree-node" :class="{ 'is-active': moveForm.folderId === data.id }">
            <i :class="data.children && data.children.length ? 'el-icon-folder-opened' : 'el-icon-folder'" style="margin-right: 4px;"></i>
            <span>{{ data.name }}</span>
            <i v-if="moveForm.folderId === data.id" class="el-icon-check batch-move-check"></i>
          </span>
        </el-tree>
      </div>
      <div style="margin-top: 12px;">
        {{ $t('consumption.batchMoveTarget') }}:
        <strong>{{ getMoveTargetLabel() }}</strong>
      </div>
      <div slot="footer">
        <el-button @click="moveDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="moveSaving" @click="submitMove">{{ $t('consumption.batchMoveConfirm') }}</el-button>
      </div>
    </el-dialog>

    <!-- 素材预览 -->
    <el-dialog :visible.sync="previewVisible" width="800px" append-to-body>
      <img v-if="previewItem && previewItem.type === 'image'" :src="previewItem.filePath" style="max-width:100%;max-height:70vh" />
      <video v-else-if="previewItem" :src="previewItem.filePath" controls style="max-width:100%;max-height:70vh"></video>
    </el-dialog>
  </div>
</template>

<script>
import { listMaterialConsumption, exportMaterialConsumption, queryUploaderOptions } from '@/api/report/materialConsumption';
import { confirmExportLargeRows } from '@/utils/export';
import { batchUpdateMaterialFolder, getConsumptionDaily } from '@/api/material/material';
import { queryCreatorOptions } from '@/api/report/insights';
import { listStrategies } from '@/api/report/strategyAnalysis';
import { listTags } from '@/api/material/tag';
import { listFolderTree } from '@/api/material/folder';
import { withLocalDevLongRangeDateShortcuts } from '@/utils/dateRangePickerOptions';
import Pagination from '@/components/Pagination';
import RightToolbar from '@/components/RightToolbar';
import TableOperations from '@/components/TableOperations';

export default {
  name: 'MaterialConsumption',
  components: { Pagination, RightToolbar, TableOperations },
  data() {
    return {
      loading: true,
      showSearch: true,
      /** 指标范围筛选折叠（与书籍汇总「指标筛选」一致） */
      showMetricFilter: false,
      materialList: [],
      tagOptions: [],
      uploaderOptions: [],
      creatorOptions: [],
      strategyList: [],
      total: 0,
      exporting: false,
      queryInfo: null,
      ckDiagnostic: null,
      globalSummary: null,
      selection: [],
      dateRange: [],
      ctrMinPercent: undefined,
      ctrMaxPercent: undefined,
      moveDialogVisible: false,
      moveSaving: false,
      moveForm: {
        folderId: 0
      },
      folderTree: [],
      folderOptions: [],
      datePickerOptions: withLocalDevLongRangeDateShortcuts([
        { text: this.$t('consumption.last7days'), onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 6); picker.$emit('pick', [s, e]); } },
        { text: this.$t('consumption.last30days'), onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 29); picker.$emit('pick', [s, e]); } },
        { text: this.$t('consumption.last90days'), onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 89); picker.$emit('pick', [s, e]); } }
      ]),
      query: {
        name: undefined,
        ids: [],
        externalId: undefined,
        type: undefined,
        tagIds: [],
        createdBy: undefined,
        adAccountId: undefined,
        platform: undefined,
        fbAdId: undefined,
        bookName: undefined,
        userName: undefined,
        strategyId: undefined,
        adCountMin: undefined,
        adCountMax: undefined,
        spendMin: undefined,
        spendMax: undefined,
        ctrMin: undefined,
        ctrMax: undefined,
        purchasesMin: undefined,
        purchasesMax: undefined,
        roiMin: undefined,
        roiMax: undefined,
        costPerPurchaseMin: undefined,
        costPerPurchaseMax: undefined,
        startDate: undefined,
        endDate: undefined,
        sortField: undefined,
        sortOrder: undefined,
        onlyWithConsumption: true,
        pageNo: 1,
        pageSize: 10
      },
      // 明细弹窗
      detailVisible: false,
      detailLoading: false,
      detailMaterial: null,
      dailyList: [],
      // 预览
      previewVisible: false,
      previewItem: null
    };
  },
  computed: {
    isDataLimited() {
      var appVm = this.$root && this.$root.$children && this.$root.$children[0];
      var user = appVm && appVm.currentUser;
      if (!user || !user.roleCodes) return false;
      return user.roleCodes.indexOf('data_limited') !== -1;
    },
    moveFolderTreeData() {
      return this.folderTree || [];
    },
    summary() {
      const s = this.globalSummary;
      if (s) {
        return {
          spend: Number(s.spend) || 0,
          impressions: Number(s.impressions) || 0,
          clicks: Number(s.clicks) || 0,
          purchases: Number(s.purchases) || 0,
          purchaseValue: Number(s.purchase_value != null ? s.purchase_value : s.purchaseValue) || 0
        };
      }
      let spend = 0, impressions = 0, clicks = 0, purchases = 0, purchaseValue = 0;
      for (const row of this.materialList) {
        spend += Number(row.spend) || 0;
        impressions += Number(row.impressions) || 0;
        clicks += Number(row.clicks) || 0;
        purchases += Number(row.purchases) || 0;
        purchaseValue += Number(row.purchaseValue) || 0;
      }
      return { spend, impressions, clicks, purchases, purchaseValue };
    }
  },
  created() {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6);
    const fmt = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    this.dateRange = [fmt(start), fmt(end)];
    this.query.startDate = fmt(start);
    this.query.endDate = fmt(end);
    this.fetchFolders();
    this.fetchTags();
    this.loadFilterOptions();
    this.fetchList();
  },
  methods: {
    async fetchTags() {
      try {
        const response = await listTags({ pageSize: 1000 });
        this.tagOptions = response.data.records || response.data || [];
      } catch (e) {
        console.error('Failed to fetch tags', e);
      }
    },
    async fetchFolders() {
      try {
        const response = await listFolderTree({ filterByOwner: true });
        const tree = response.data || [];
        this.folderTree = tree;
        this.folderOptions = this.flattenFolderOptions(tree);
      } catch (e) {
        console.error('Failed to fetch folders', e);
      }
    },
    flattenFolderOptions(list, level = 0, result = []) {
      list.forEach(item => {
        const prefix = level > 0 ? `${'--'.repeat(level)} ` : '';
        result.push({ id: item.id, label: `${prefix}${item.name}` });
        if (item.children && item.children.length) {
          this.flattenFolderOptions(item.children, level + 1, result);
        }
      });
      return result;
    },
    loadFilterOptions() {
      queryCreatorOptions().then(function(res) {
        this.creatorOptions = res.data || [];
      }.bind(this));
      queryUploaderOptions().then(function(res) {
        this.uploaderOptions = res.data || [];
      }.bind(this));
      listStrategies().then(function(res) {
        this.strategyList = res.data || [];
      }.bind(this));
    },
    async fetchList() {
      this.loading = true;
      this.query.ctrMin = this.ctrMinPercent != null ? this.ctrMinPercent / 100 : undefined;
      this.query.ctrMax = this.ctrMaxPercent != null ? this.ctrMaxPercent / 100 : undefined;
      try {
        const res = await listMaterialConsumption(this.query);
        const d = res.data || {};
        this.materialList = d.records || [];
        this.total = d.total || this.materialList.length;
        this.globalSummary = d.summary || null;
        if (d.engine) {
          this.queryInfo = { engine: d.engine, queryMs: d.queryMs || 0, total: d.total || 0 };
        }
        this.ckDiagnostic = d.diagnostic || null;
      } finally {
        this.loading = false;
      }
    },
    handleQuery() {
      this.query.pageNo = 1;
      this.selection = [];
      this.fetchList();
    },
    /**
     * 规范化数字 ID：拆分逗号/空格/换行，去重，只保留正整数。
     * 回车累加标签后会触发 change；粘贴多 ID 也会走这里。
     */
    normalizeMaterialIds(rawList) {
      const result = [];
      const seen = new Set();
      (rawList || []).forEach(item => {
        String(item == null ? '' : item)
          .split(/[,，;\s\n\r]+/)
          .forEach(part => {
            const id = String(part || '').trim();
            if (!/^\d+$/.test(id) || seen.has(id)) {
              return;
            }
            seen.add(id);
            // 以 Number 存储，请求参数与后端 List<Long> 对齐
            result.push(Number(id));
          });
      });
      return result;
    },
    onMaterialIdsChange(val) {
      const normalized = this.normalizeMaterialIds(val);
      const prev = (this.query.ids || []).map(String).join(',');
      const next = normalized.map(String).join(',');
      this.query.ids = normalized;
      // 内容变化时自动搜索：回车加一个 / 删一个 / 清空，都立刻刷新列表
      if (prev !== next) {
        this.handleQuery();
      }
    },
    /** 粘贴「2999,3688 3461」时拆成多个标签 */
    onMaterialIdsPaste(e) {
      const text = (e.clipboardData && e.clipboardData.getData('text')) || '';
      if (!text || !/[,，;\s\n\r]/.test(text)) {
        return;
      }
      e.preventDefault();
      const merged = this.normalizeMaterialIds([].concat(this.query.ids || [], [text]));
      this.query.ids = merged;
      this.handleQuery();
    },
    resetQuery() {
      this.showMetricFilter = false;
      this.dateRange = [];
      this.ctrMinPercent = undefined;
      this.ctrMaxPercent = undefined;
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 6);
      const fmt = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      this.dateRange = [fmt(start), fmt(end)];
      this.query = {
        name: undefined,
        ids: [],
        externalId: undefined,
        type: undefined,
        tagIds: [],
        createdBy: undefined,
        adAccountId: undefined,
        platform: undefined,
        fbAdId: undefined,
        bookName: undefined,
        userName: undefined,
        strategyId: undefined,
        adCountMin: undefined,
        adCountMax: undefined,
        spendMin: undefined,
        spendMax: undefined,
        ctrMin: undefined,
        ctrMax: undefined,
        purchasesMin: undefined,
        purchasesMax: undefined,
        roiMin: undefined,
        roiMax: undefined,
        costPerPurchaseMin: undefined,
        costPerPurchaseMax: undefined,
        startDate: fmt(start),
        endDate: fmt(end),
        sortField: undefined,
        sortOrder: undefined,
        onlyWithConsumption: true,
        pageNo: 1,
        pageSize: 20
      };
      this.queryInfo = null;
      this.ckDiagnostic = null;
      this.globalSummary = null;
      this.selection = [];
      this.handleQuery();
    },
    handleDateChange(val) {
      if (val && val.length === 2) {
        this.query.startDate = val[0];
        this.query.endDate = val[1];
      } else {
        this.query.startDate = undefined;
        this.query.endDate = undefined;
      }
    },
    handleSortChange({ prop, order }) {
      if (prop && order) {
        this.query.sortField = prop;
        this.query.sortOrder = order === 'ascending' ? 'asc' : 'desc';
      } else {
        this.query.sortField = undefined;
        this.query.sortOrder = undefined;
      }
      this.query.pageNo = 1;
      this.fetchList();
    },
    async handleExport() {
      this.$confirm(this.$t('consumption.exportConfirm'), this.$t('consumption.exportTip'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        const countQuery = {
          ...this.query,
          pageNo: 1,
          pageSize: 1,
          ctrMin: this.ctrMinPercent != null ? this.ctrMinPercent / 100 : undefined,
          ctrMax: this.ctrMaxPercent != null ? this.ctrMaxPercent / 100 : undefined
        };
        let countRes;
        try {
          countRes = await listMaterialConsumption(countQuery);
        } catch (e) {
          this.$message.error(this.$t('common.exportCountFail'));
          return;
        }
        try {
          await confirmExportLargeRows(this, (countRes.data || {}).total || 0);
        } catch (e) {
          return;
        }
        this.exporting = true;
        try {
          const params = { ...this.query };
          delete params.pageNo;
          delete params.pageSize;
          params.ctrMin = this.ctrMinPercent != null ? this.ctrMinPercent / 100 : undefined;
          params.ctrMax = this.ctrMaxPercent != null ? this.ctrMaxPercent / 100 : undefined;
          const res = await exportMaterialConsumption(params);
          const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = this.$t('consumption.exportFileName');
          link.click();
          window.URL.revokeObjectURL(url);
          this.$message.success(this.$t('consumption.exportSuccess'));
        } catch (e) {
          this.$message.error(this.$t('consumption.exportFail'));
        } finally {
          this.exporting = false;
        }
      }).catch(() => {});
    },
    async handleDetail(row) {
      this.detailMaterial = row;
      this.detailVisible = true;
      this.detailLoading = true;
      try {
        const params = {};
        if (this.query.startDate) params.startDate = this.query.startDate;
        if (this.query.endDate) params.endDate = this.query.endDate;
        const res = await getConsumptionDaily(row.id, params);
        this.dailyList = res.data || [];
      } finally {
        this.detailLoading = false;
      }
    },
    handlePreview(item) {
      this.previewItem = item;
      this.previewVisible = true;
    },
    formatMoney(val) {
      if (val == null) return '-';
      return Number(val).toFixed(2);
    },
    formatDecimal(val) {
      if (val == null) return '-';
      return Number(val).toFixed(4);
    },
    formatPercent(val) {
      if (val == null) return '-';
      return (Number(val) * 100).toFixed(2) + '%';
    },
    formatInt(val) {
      if (val == null) return '-';
      return Number(val).toLocaleString();
    },
    handleSelectionChange(val) {
      this.selection = val;
    },
    handleBatchMove() {
      if (this.selection.length === 0) {
        this.$message.warning(this.$t('consumption.batchMoveNoSelection'));
        return;
      }
      this.moveForm.folderId = 0;
      this.moveDialogVisible = true;
    },
    getMoveTargetLabel() {
      if (!this.moveForm.folderId || this.moveForm.folderId === 0) {
        return this.$t('consumption.unfiled');
      }
      const found = this.folderOptions.find(item => item.id === this.moveForm.folderId);
      if (!found) {
        return this.$t('consumption.unfiled');
      }
      return found.label.replace(/^[-\s]+/, '');
    },
    selectMoveFolder(folderId) {
      this.moveForm.folderId = folderId;
    },
    onMoveFolderTreeClick(data) {
      this.moveForm.folderId = data.id;
    },
    async submitMove() {
      if (this.selection.length === 0) {
        this.$message.warning(this.$t('consumption.batchMoveNoSelection'));
        return;
      }
      this.moveSaving = true;
      try {
        const ids = this.selection.map(item => item.id);
        const folderId = this.moveForm.folderId === 0 ? null : this.moveForm.folderId;
        await batchUpdateMaterialFolder(ids, folderId);
        this.moveDialogVisible = false;
        this.selection = [];
        await this.fetchList();
        this.$message.success(this.$t('consumption.batchMoveSuccess'));
      } catch (e) {
        this.$message.error(this.$t('consumption.batchMoveFail'));
      } finally {
        this.moveSaving = false;
      }
    },
    splitTagNames(tagNames) {
      if (!tagNames) return [];
      return String(tagNames)
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    }
  }
};
</script>

<style scoped>
.material-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.material-thumb {
  width: 48px;
  height: 36px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
}
.material-info {
  overflow: hidden;
}
.material-name {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}
.batch-move-tip {
  margin-bottom: 8px;
  color: #606266;
  display: flex;
  align-items: center;
}
.batch-move-tree-wrap {
  max-height: 320px;
  overflow: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 6px;
  background: #fff;
}
.batch-move-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 4px;
  cursor: pointer;
  border-radius: 4px;
  margin-bottom: 4px;
}
.batch-move-tree-node {
  display: flex;
  align-items: center;
  gap: 6px;
}
.batch-move-item.is-active,
.batch-move-tree-node.is-active {
  background: #ecf5ff;
  color: #409eff;
}
.batch-move-check {
  margin-left: auto;
  color: #67c23a;
}

/* 指标范围筛选行（与书籍汇总 query-card 内样式一致） */
.metric-filter-row {
  border-top: 1px dashed #dcdfe6;
  padding-top: 10px;
  margin-top: 4px;
}
.range-sep {
  margin: 0 4px;
  color: #909399;
}
.range-unit {
  margin-left: 2px;
  font-size: 12px;
  color: #909399;
}
.summary-row {
  margin-top: 12px;
  padding: 10px 16px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
}
.summary-label {
  font-weight: 600;
  color: #303133;
}
.summary-row b {
  color: #409EFF;
}
</style>
